/* eslint-disable no-underscore-dangle */
import breakpoints from '@/js/utils/breakpoints';

/**
 * Accordion component.
 */
export default class Accordion {
  /**
   * Get the target element based on the controller's `aria-controls` attribute.
   *
   * @param  {HTMLButtonElement} controller The component's controlling element.
   * @return {HTMLElement|null}
   */
  static getTargetElement(controller) {
    if (!controller.hasAttribute('aria-controls')) {
      const message = 'The component element is missing the required \'aria-controls\' attribute';
      throw new Error(`Configuration error: ${message}`);
    }

    const targetId = controller.getAttribute('aria-controls');
    const target = document.getElementById(targetId);

    if (target === null) {
      const message = `A target element with ID of '${targetId}' is not found`;
      throw new Error(`Configuration error: ${message}`);
    }

    return target;
  }

  /**
   * Start the component
   */
  constructor(controller, options = {}) {
    // Collect elements.
    this.controller = controller;
    this.target = this.constructor.getTargetElement(controller);

    // Bind class methods.
    this.init = this.init.bind(this);
    this.setup = this.setup.bind(this);
    this.setState = this.setState.bind(this);
    this.patchButtonKeydownSupport = this.patchButtonKeydownSupport.bind(this);
    this.controllerHandleClick = this.controllerHandleClick.bind(this);
    this.stateWasUpdated = this.stateWasUpdated.bind(this);
    this.toggle = this.toggle.bind(this);
    this.destroy = this.destroy.bind(this);

    /**
     * Component configuration options.
     *
     * @type {object}
     */
    const defaultOptions = {
      /**
       * The breakpoint to initialize the component.
       *
       * @type {string}
       */
      breakpoint: '',
      /**
       * The classname to add to the target when it is shown.
       *
       * @type {string}
       */
      shownClass: 'accordion__target--is-shown',
      /**
       * Whether or not the togle should be open by default.
       *
       * @type {Boolean}
       */
      loadOpen: false,
    };

    // Merge options with defaults.
    this.options = Object.assign(defaultOptions, options);

    /**
     * Component state.
     *
     * @type {object}
     */
    this.state = {
      expanded: this.options.loadOpen,
    };

    /**
     * The MediaQueryList based on the breakpoint value.
     *
     * @type {MediaQueryList|undefined}
     */
    this.mediaQuery = breakpoints[options.breakpoint];

    this.init();
    if (this.mediaQuery) {
      this.mediaQuery.addEventListener('change', this.init);
    }
  }

  /**
   * Initialization traffic cop.
   */
  init() {
    if (!this.mediaQuery || this.mediaQuery?.matches) {
      // There's no breakpoint set up, or there is and it matches.
      this.setup();
    } else {
      this.destroy();
    }
  }

  /**
   * Set up the component's DOM attributes and event listeners.
   */
  setup() {
    const { expanded } = this.state;

    // Set the initial controller attributes.
    requestAnimationFrame(() => {
      this.controller.setAttribute('aria-expanded', expanded);
    });

    // Patch button role for non-button controller.
    if (this.controller.nodeName !== 'BUTTON') {
      this.controller.setAttribute('role', 'button');
      this.controller.addEventListener('keydown', this.patchButtonKeydownSupport);
    }

    /*
     * Set the taget as hidden by default. Using the `aria-hidden` attribute,
     * rather than the `hidden` attribute, means authors must hide the target
     * element via CSS.
     */
    requestAnimationFrame(() => {
      this.target.setAttribute('aria-hidden', !expanded);
    });
    this.target.setAttribute('aria-labelledby', this.controller.id);

    /**
     * Flag to avoid overwriting/removing an existing role attribute.
     *
     * @type {bool}
     */
    this._overwriteRoleAttribute = (!this.target.hasAttribute('role'));

    // Don't overwite an existing role attribute.
    if (this._overwriteRoleAttribute) {
      this.target.setAttribute('role', 'region');
    }

    // Add event listeners
    this.controller.addEventListener('click', this.controllerHandleClick);
  }

  /**
   * Set component state.
   *
   * @param {object} newState The new state to merge with existing state.
   */
  setState(newState) {
    const updatedState = { ...this.state, ...newState };
    this.state = updatedState;

    this.stateWasUpdated(updatedState);
  }

  /**
   * Update the component attributes based on updated state.
   *
   * @param {object} state The component state.
   */
  stateWasUpdated({ expanded }) {
    const { shownClass } = this.options;
    requestAnimationFrame(() => {
      this.controller.setAttribute('aria-expanded', `${expanded}`);
    });

    // Update target attributes and activeElement.
    if (expanded) {
      requestAnimationFrame(() => {
        this.target.setAttribute('aria-hidden', 'false');
        this.target.classList.add(shownClass);
      });
    } else {
      requestAnimationFrame(() => {
        this.target.setAttribute('aria-hidden', 'true');
        this.target.classList.remove(shownClass);
      });
    }
  }

  /**
   * Handle keydown events on the ToggleButton controller.
   *
   * @param {Event} event The event object.
   */
  patchButtonKeydownSupport(event) {
    const { code } = event;

    if (['Space', 'Enter'].includes(code)) {
      event.preventDefault();

      /*
       * Treat the Spacebar and Return keys as clicks in case the controller is
       * not a <button>.
       */
      this.toggle();
    }
  }

  /**
   * Handle click events on toggle buttons.
   *
   * @param {Event} event The Event object.
   */
  controllerHandleClick(event) {
    event.preventDefault();
    this.toggle();
  }

  /**
   * ToggleButton component state.
   */
  toggle() {
    const { expanded } = this.state;

    this.setState({ expanded: !expanded });
  }

  /**
   * Remove all attributes and event listeners added by this class.
   */
  destroy() {
    const { shownClass } = this.options;

    // Remove controller attributes.
    requestAnimationFrame(() => {
      this.controller.removeAttribute('aria-expanded');
    });

    // Remove target attributes.
    requestAnimationFrame(() => {
      this.target.removeAttribute('aria-hidden');
      this.target.classList.remove(shownClass);
    });
    if (this._overwriteRoleAttribute) {
      this.target.removeAttribute('role');
    }
    this.target.removeAttribute('aria-labelledby');

    // Remove event listeners.
    this.controller.removeEventListener('click', this.controllerHandleClick);
    this.controller.removeEventListener('keydown', this.patchButtonKeydownSupport);

    if (this.mediaQuery) {
      this.mediaQuery.removeEventListener('change', this.handleBreakpointChange);
    }

    // Reset initial state.
    this.state = {
      expanded: this.options.loadOpen,
    };
  }
}
